import {
  Upload,
  X,
} from 'lucide-react';
import InputForm from '../../common/Inputs/InputForm/InputForm';
import { UpsellProduct } from '../steps/step-3-additional-offers';
import { AdditionalOffers } from '../../../types/onboarding';
import { useState } from 'react';
import { TypeProduct } from '../steps/step-1-product-data';
import ImportProductFromLinkHeader from './ImportProductFromLinkHeader';
import SearchPreviousProducts from './SearchPreviousProducts';
import RenderEntryMethodSelection from './RenderEntryMethodSelection';
import ButtonResetTypeProduct from './ButtonResetTypeProdct';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';


interface RenderProductFormUpsellProps {
  product: UpsellProduct;
  isUpsell: boolean;
  upsellId: string;
  handleImageUpload: (upsellId: string) => void;
  upsells: UpsellProduct[];
  setUpsells: (upsells: UpsellProduct[]) => void;
  bumpOffer: any;
  setBumpOffer: (bumpOffer: any) => void;
  additionalOffers: AdditionalOffers;
  updateAdditionalOffers: (data: AdditionalOffers) => void;
  upsellToggleEnabled: boolean;
  handleUpsellChange: (upsellId: string, field: keyof UpsellProduct, value: string) => void;
  handleBumpOfferChange: (field: string, value: string) => void;
  previousProducts: any[];
  isPreviousProductsLoading: boolean;
  handlePreviousUpsellSelect: (product: any, upsellId: string) => void;
}

export default function RenderProductFormUpsell({
  upsells,
  setUpsells,
  bumpOffer,
  setBumpOffer,
  additionalOffers,
  updateAdditionalOffers,
  upsellToggleEnabled,
  product,
  isUpsell,
  upsellId,
  handleImageUpload,
  handleUpsellChange,
  handleBumpOfferChange,
  previousProducts,
  isPreviousProductsLoading,
  handlePreviousUpsellSelect
}: RenderProductFormUpsellProps) {
  const [typeProduct, setTypeProduct] = useState<TypeProduct>(TypeProduct.NONE);
  const { language } = useAuth();
  
  const handleTypeProductChange = (type: TypeProduct) => {
    setTypeProduct(type);
  };

  const handlePreviousProductSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const productId = e.target.value;
    if (!productId) return;
    const selectedProduct = previousProducts.find(product => product.id === productId);
    if (!selectedProduct) return;
    handlePreviousUpsellSelect(selectedProduct, upsellId);
  };

  return (
    <div className="space-y-4 mt-4 border-t border-gray-200 pt-4">

      {typeProduct !== TypeProduct.NONE && (
        <div className="flex justify-end">
          <ButtonResetTypeProduct
            onClick={() => setTypeProduct(TypeProduct.NONE)}
          />
        </div>
      )}

      {typeProduct === TypeProduct.NONE && (
        <RenderEntryMethodSelection
          typeProduct={typeProduct}
          handleLinkEntry={() => handleTypeProductChange(TypeProduct.LINK)}
          handleManualEntry={() => handleTypeProductChange(TypeProduct.MANUAL)}
          handlePreviousProductSelectType={() => handleTypeProductChange(TypeProduct.PREVIOUS)}
        />
      )}

      {typeProduct === TypeProduct.LINK && (
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          {product.productName ? (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium">Selected Product</h4>
                <button
                  type="button"
                  onClick={() => {
                    if (isUpsell) {
                      const currentUpsell = upsells.find(item => item.id === upsellId);
                      if (!currentUpsell) return;

                      const resetUpsell = {
                        ...currentUpsell,
                        productName: '',
                        price: '',
                        description: '',
                        image: undefined,
                        productLink: '',
                        isManualEntry: false,
                        error: '',
                        isLoading: false
                      };

                      // Update all fields at once to avoid multiple renders
                      const updatedUpsells = upsells.map(item =>
                        item.id === upsellId ? resetUpsell : item
                      );
                      setUpsells(updatedUpsells);

                      // Update store
                      updateAdditionalOffers({
                        ...additionalOffers,
                        upsell: {
                          enabled: upsellToggleEnabled,
                          products: updatedUpsells
                        }
                      });
                    } else {
                      // Reset order bump to initial state
                      const resetBumpOffer = {
                        ...bumpOffer,
                        url_images: undefined,
                        productName: '',
                        price: '',
                        description: '',
                        image: undefined,
                        productLink: '',
                        isManualEntry: false,
                        error: '',
                        isLoading: false,
                        // Preserve the enabled state
                        enabled: bumpOffer.enabled
                      };

                      setBumpOffer(resetBumpOffer);

                      // Update store
                      updateAdditionalOffers({
                        ...additionalOffers,
                        bumpOffer: resetBumpOffer
                      });
                    }
                  }}
                  className="btn btn-secondary"
                >
                  Change Product
                </button>
              </div>

              <div className="border rounded-lg p-5">
                <div className="flex items-center">
                  {product.image && (
                    <div className="w-12 h-12 rounded-lg overflow-hidden mr-3">
                      <img
                        src={product.image}
                        alt={product.productName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium text-lg">
                      {product.productName.split(' ').slice(0, 8).join(' ') +
                        (product.productName.split(' ').length > 8 ? '...' : '')}
                    </h4>
                    <p className="text-gray-600 text-sm">${product.price}</p>
                    <p className="text-gray-600 text-sm mt-2">
                      {product.description.split(' ').slice(0, 15).join(' ') +
                        (product.description.split(' ').length > 15 ? '...' : '')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ImportProductFromLinkHeader name="upsell" />
          )}
          {product.isLoading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <p className="text-gray-500 mt-4">Searching your product...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {!product.productName && (
                <div>
                  <input
                    type="text"
                    className="input"
                    placeholder={dictionary["paste-product-url-placeholder"][language]}
                    value={product.productLink}
                    onChange={(e) => {
                      if (isUpsell) {
                        handleUpsellChange(upsellId!, 'productLink', e.target.value);
                      } else {
                        handleBumpOfferChange('productLink', e.target.value);
                      }
                    }}
                  />
                  {product.error && (
                    <p className="text-sm text-red-500 mt-2">{product.error}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {typeProduct === TypeProduct.MANUAL && (
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="form-group">
              <label className="label">{dictionary["product-name"][language]}</label>
              <div className="w-full rounded-xl border border-gray-200 overflow-hidden">
                <InputForm
                  name="upsell-productName"
                  placeholder={dictionary["enter-product-name-placeholder"][language]}
                  value={product.productName}
                  onChange={(e) => handleUpsellChange(upsellId!, 'productName', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="label">{dictionary["price"][language]}</label>
              <div className="w-full rounded-xl border border-gray-200 overflow-hidden">
                <InputForm
                  type='number'
                  name="upsell-price"
                  placeholder={dictionary["enter-price-placeholder"][language]}
                  value={product.price}
                  onChange={(e) => handleUpsellChange(upsellId!, 'price', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="label">{dictionary["description"][language]}</label>
            <div className="w-full rounded-xl border border-gray-200 overflow-hidden">
              <InputForm
                name="upsell-description"
                placeholder={dictionary["enter-description-placeholder"][language]}
                value={product.description}
                onChange={(e) => handleUpsellChange(upsellId!, 'description', e.target.value)}
              />
            </div>
          </div>

          <div className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center ${isUpsell ? 'border-primary-200' : 'border-emerald-200'
            }`}>
            {product.image ? (
              <div className="relative w-full max-w-xs">
                <img
                  src={product.image}
                  alt={isUpsell ? "Complementary product" : "order bump product"}
                  className="w-full h-48 object-contain"
                />
                <button
                  type="button"
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                  onClick={() => {
                    if (isUpsell && upsellId) {
                      handleUpsellChange(upsellId, 'image', '');
                    } else {
                      handleBumpOfferChange('image', '');
                    }
                  }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleImageUpload(isUpsell ? upsellId : '')}
                  className={`btn mb-4 ${isUpsell ? 'btn-primary' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                >
                  <Upload className="w-5 h-5 mr-2" />
                  {dictionary["upload-product-image"][language]}
                </button>
                <div className="flex items-center text-gray-500 text-sm">
                  <span>{dictionary["upload-an-image-of-your-product"][language]}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {typeProduct === TypeProduct.PREVIOUS && (
        <SearchPreviousProducts
          previousProducts={previousProducts}
          isPreviousProductsLoading={isPreviousProductsLoading}
          handlePreviousProductSelect={handlePreviousProductSelect}
        />
      )}
    </div>
  );
};